import React, { useState, useEffect } from 'react';
import { 
  Shield, FileCheck, Award, Mail, Menu, X, ArrowRight, Globe, CheckCircle
} from 'lucide-react';

const Nav = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav className={`fixed w-full z-50 transition-all duration-500 ${
      isScrolled ? 'bg-white/95 backdrop-blur-md shadow-xl py-3' : 'bg-transparent py-6'
    }`}>
      <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
        <a href="#home" className="flex items-center space-x-3">
          <div className="relative w-12 h-12 flex items-center justify-center bg-blue-600 rounded-xl">
            <Shield className="w-7 h-7 text-white" />
          </div>
          <div>
            <span className={`font-black text-2xl tracking-tight ${isScrolled ? 'text-slate-900' : 'text-white'}`}>TADANAI</span>
            <div className={`text-xs font-semibold tracking-wider ${isScrolled ? 'text-slate-500' : 'text-blue-200'}`}>GRC & Cyber Security</div>
          </div>
        </a>

        <div className={`hidden md:flex items-center space-x-8 text-sm font-bold uppercase tracking-widest ${isScrolled ? 'text-slate-700' : 'text-slate-200'}`}>
          <a href="#about" className="hover:text-blue-500 transition-colors">About</a>
          <a href="#services" className="hover:text-blue-500 transition-colors">Services</a>
          <a href="#packages" className="hover:text-blue-500 transition-colors">Packages</a>
          <a href="#contact" className="bg-blue-600 text-white px-7 py-3 rounded-full hover:bg-blue-700 transition shadow-lg">Consult Now</a>
        </div>

        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className={`md:hidden ${isScrolled ? 'text-slate-900' : 'text-white'}`}>
          {mobileMenuOpen ? <X className="w-8 h-8" /> : <Menu className="w-8 h-8" />}
        </button>
      </div>
      
      {mobileMenuOpen && (
        <div className="md:hidden bg-white absolute top-full left-0 w-full p-6 shadow-2xl flex flex-col space-y-4 font-bold text-slate-900 border-t">
          <a href="#about" onClick={() => setMobileMenuOpen(false)}>About</a>
          <a href="#services" onClick={() => setMobileMenuOpen(false)}>Services</a>
          <a href="#packages" onClick={() => setMobileMenuOpen(false)}>Packages</a>
          <a href="#contact" className="text-blue-600" onClick={() => setMobileMenuOpen(false)}>Consult Now</a>
        </div>
      )}
    </nav>
  );
};

export default function App() {
  return (
    <div className="min-h-screen bg-white font-sans antialiased scroll-smooth">
      <Nav />
      
      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center bg-slate-900 overflow-hidden text-white pt-24">
        <div className="absolute inset-0 bg-[url('https://transparenttextures.com')] opacity-10"></div>
        <div className="max-w-7xl mx-auto px-6 relative z-10 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="inline-flex items-center space-x-2 bg-blue-500/10 border border-blue-400/30 px-5 py-2.5 rounded-full mb-8">
              <Globe className="w-5 h-5 text-blue-400" />
              <span className="text-blue-300 text-sm font-bold uppercase tracking-widest">Global GRC Excellence</span>
            </div>
            <h1 className="text-6xl md:text-8xl font-black mb-8 leading-[0.9] tracking-tighter">
              Securing <br/><span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400">Innovation.</span>
            </h1>
            <p className="text-xl text-slate-400 mb-10 leading-relaxed max-w-xl">
              Tadanai Limited: Bridging complex regulatory requirements and robust digital resilience across the UK, Europe, and Africa.
            </p>
            <div className="flex flex-col sm:flex-row gap-5">
              <a href="#services" className="bg-blue-600 text-white px-10 py-5 rounded-2xl font-black flex items-center justify-center hover:bg-blue-700 transition">
                Explore Services <ArrowRight className="ml-2 w-5 h-5" />
              </a>
              <a href="#about" className="bg-white/10 border border-white/20 px-10 py-5 rounded-2xl font-black hover:bg-white/20 transition text-center">
                About Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-5 gap-16 items-start">
          <div className="lg:col-span-2">
            <h2 className="text-blue-600 font-black tracking-widest text-xs uppercase mb-4 italic">The Mission</h2>
            <h3 className="text-5xl font-black text-slate-900 tracking-tighter mb-8 leading-tight">About Tadanai Limited.</h3>
            <div className="p-6 bg-slate-50 rounded-2xl border border-slate-100 flex items-center space-x-4">
               <Award className="w-10 h-10 text-blue-600" />
               <span className="font-bold text-slate-800 italic">10+ Years International Consultancy</span>
            </div>
          </div>
          <div className="lg:col-span-3 space-y-6 text-lg text-slate-600 leading-relaxed">
            <p>At Tadanai Limited, we bridge the gap between complex regulatory requirements and robust digital resilience. Led by experts with over 10 years of international experience, we provide high-level consultancy across the <strong>UK, Europe, and Africa.</strong></p>
            <p>We empower organisations to navigate the evolving threat landscape by aligning their IT infrastructure with global standards like <strong>NIST, ISO 27001, GDPR, and DORA.</strong> Whether migrating to the cloud or hardening industrial control systems, Tadanai is your partner in building a secure, compliant future.</p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-20">
            <h2 className="text-blue-600 font-black tracking-widest text-xs uppercase mb-4">What We Do</h2>
            <h3 className="text-5xl font-black text-slate-900 tracking-tighter">Cyber Strategy & Compliance.</h3>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="space-y-6">
              <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center">
                <FileCheck className="w-8 h-8 text-blue-600" />
              </div>
              <h4 className="text-2xl font-bold text-slate-900">Governance & Privacy</h4>
              <p className="text-slate-600">Specialised implementation of <strong>UK GDPR, NIS2, and DORA</strong> frameworks. We conduct deep-dive gap analysis to ensure your data handling meets international legal standards.</p>
            </div>
            
            <div className="space-y-6">
              <div className="w-16 h-16 bg-indigo-50 rounded-2xl flex items-center justify-center">
                <Shield className="w-8 h-8 text-indigo-600" />
              </div>
              <h4 className="text-2xl font-bold text-slate-900">Risk & Resilience</h4>
              <p className="text-slate-600">End-to-end <strong>ISO 27001</strong> certification support. We build Threat & Risk Assessments (TRA) and Disaster Recovery plans that keep your operations running through any crisis.</p>
            </div>

            <div className="space-y-6">
              <div className="w-16 h-16 bg-slate-900 rounded-2xl flex items-center justify-center">
                <Mail className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-2xl font-bold text-slate-900">Strategic Support</h4>
              <p className="text-slate-600">Fractional CISO services and security architecture reviews. We provide the leadership you need to scale safely and protect your innovation.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Packages Section */}
      <section id="packages" className="py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-blue-600 font-black tracking-widest text-xs uppercase mb-4 italic">Pricing</h2>
          <h3 className="text-5xl font-black text-slate-900 tracking-tighter mb-16">Consultancy Packages.</h3>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-white p-10 rounded-3xl border border-slate-200 text-left shadow-lg">
              <h4 className="text-2xl font-bold mb-4 italic text-blue-600">Compliance Baseline</h4>
              <ul className="space-y-4 mb-8 text-slate-600">
                <li className="flex items-center space-x-3"><CheckCircle className="w-5 h-5 text-blue-500" /> <span>GDPR Gap Analysis</span></li>
                <li className="flex items-center space-x-3"><CheckCircle className="w-5 h-5 text-blue-500" /> <span>Policy Development</span></li>
                <li className="flex items-center space-x-3"><CheckCircle className="w-5 h-5 text-blue-500" /> <span>Security Awareness Training</span></li>
              </ul>
              <a href="#contact" className="block text-center bg-blue-600 text-white py-4 rounded-xl font-bold">Inquire Now</a>
            </div>
            
            <div className="bg-slate-900 p-10 rounded-3xl text-left shadow-2xl text-white">
              <h4 className="text-2xl font-bold mb-4 italic text-blue-400">Strategic GRC</h4>
              <ul className="space-y-4 mb-8 text-slate-300">
                <li className="flex items-center space-x-3"><CheckCircle className="w-5 h-5 text-blue-400" /> <span>ISO 27001 Implementation</span></li>
                <li className="flex items-center space-x-3"><CheckCircle className="w-5 h-5 text-blue-400" /> <span>Third-Party Risk Management</span></li>
                <li className="flex items-center space-x-3"><CheckCircle className="w-5 h-5 text-blue-400" /> <span>CISO Support (Fractional)</span></li>
              </ul>
              <a href="#contact" className="block text-center bg-white text-slate-900 py-4 rounded-xl font-bold">Inquire Now</a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-32 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-blue-600 font-black tracking-widest text-xs uppercase mb-4 italic">Connect</h2>
          <h3 className="text-5xl font-black text-slate-900 tracking-tighter mb-8">Ready to Secure Your Growth?</h3>
          <p className="text-xl text-slate-600 mb-12">Reach out to Tadanai Limited to discuss how we can tailor our cyber security expertise to your specific business requirements.</p>
          <div className="flex flex-col sm:flex-row justify-center gap-6">
            <a href="mailto:info@tadanai.com" className="flex items-center justify-center space-x-4 bg-blue-600 text-white px-10 py-5 rounded-2xl font-bold hover:bg-blue-700 transition shadow-xl">
              <Mail className="w-6 h-6" />
              <span>Contact Us</span>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-slate-900 border-t border-white/10 text-center text-slate-500 text-sm font-bold tracking-widest uppercase">
        <p>&copy; {new Date().getFullYear()} Tadanai Limited. All Rights Reserved.</p>
      </footer>
    </div>
  );
}
